import React, { useState, useRef, useCallback } from 'react';
import { MoreVertical, Pencil, Trash2, Calendar, User, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Story } from '../../types';
import EditStoryModal from './EditStoryModal';
import DeleteStoryModal from './DeleteStoryModal';
import StoryNotificationSettings from '../notifications/StoryNotificationSettings';
import { useStoryStore } from '../../store/storyStore';
import { formatDate, calculateAge } from '../../lib/utils';
import { useSignedUrl } from '../../hooks/useSignedUrl';
import { useClickOutside } from '../../hooks/useClickOutside';

interface StoryCardProps {
  story: Story;
}

const StoryCard: React.FC<StoryCardProps> = ({ story }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { deleteStory } = useStoryStore();
  const { url: photoUrl } = useSignedUrl(story.photo_url, 'photos');

  const closeMenu = useCallback(() => setShowMenu(false), []);
  useClickOutside(menuRef, closeMenu, showMenu);

  const handleCardClick = () => {
    navigate(`/story-recorder/${story.id}`);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowEditModal(true);
  };

  const handleNotifClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowNotifModal(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    try {
      const result = await deleteStory(story.id);

      if (!result.success) {
        console.error('Error deleting story:', result.error);
      } else {
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        data-testid="story-card"
        className={`group relative bg-white rounded-lg sm:rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-stone-200 hover:border-stone-300 touch-manipulation active:scale-98 ${
          showMenu ? 'z-30' : ''
        }`}
      >
        <div className="h-28 sm:h-32 bg-gradient-to-br from-stone-100 to-stone-200 relative overflow-hidden rounded-t-lg sm:rounded-t-xl">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={story.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <User className="h-10 w-10 sm:h-12 sm:w-12 text-stone-400 opacity-60" />
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black bg-opacity-20">
            <div
              className="h-full bg-gradient-to-r from-rose-400 to-rose-500 transition-all duration-500 shadow-sm"
              style={{ width: `${story.progress}%` }}
            />
          </div>
        </div>

        {/* Menu lives OUTSIDE the image container (and thus outside its
            overflow-hidden) so the dropdown can extend below the card
            without being clipped. The card's outer div drops its own
            overflow-hidden and lifts to z-30 when showMenu is true so the
            dropdown stacks above adjacent cards in the grid. */}
        <div className="absolute top-2 right-2 z-10" ref={menuRef}>
          <button
            onClick={handleMenuClick}
            className="h-8 w-8 flex items-center justify-center rounded-lg bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100 touch-manipulation"
            aria-label="Menú de opciones"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {showMenu && (
            <div className="absolute top-full right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-stone-200 py-1 animate-fade-in">
              <button
                onClick={handleEditClick}
                className="w-full px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-50 flex items-center space-x-2"
              >
                <Pencil className="h-4 w-4" />
                <span>Editar</span>
              </button>
              <button
                onClick={handleNotifClick}
                className="w-full px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-50 flex items-center space-x-2"
              >
                <Bell className="h-4 w-4" />
                <span>Avisos</span>
              </button>
              <button
                onClick={handleDeleteClick}
                className="w-full px-4 py-2 text-left text-sm text-rose-600 hover:bg-rose-50 flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>Eliminar</span>
              </button>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4">
          <h3 className="font-semibold text-sm sm:text-base text-stone-900 mb-2 leading-tight">
            {story.title}
          </h3>

          <div className="space-y-1.5 text-xs text-stone-600 mb-2 sm:mb-3">
            <div className="flex items-center space-x-1.5">
              <User className="h-3.5 w-3.5 mr-0.5" />
              <span className="font-medium">{story.relationship}</span>
            </div>

            {story.date_of_birth && (
              <div className="flex items-center space-x-1.5">
                <Calendar className="h-3.5 w-3.5 mr-0.5" />
                <span className="font-medium">{calculateAge(story.date_of_birth)}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-stone-500 pt-2 border-t border-stone-100">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
              <span className="font-medium">{story.progress}% completado</span>
            </div>
            <span className="text-stone-400">{formatDate(story.updated_at)}</span>
          </div>
        </div>
      </div>

      <EditStoryModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        story={story}
      />

      <DeleteStoryModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        storyTitle={story.title}
        isDeleting={isDeleting}
      />

      <StoryNotificationSettings
        isOpen={showNotifModal}
        onClose={() => setShowNotifModal(false)}
        storyId={story.id}
        storyTitle={story.title}
      />
    </>
  );
};

export default React.memo(StoryCard);
